import { useState, useContext, useEffect, useMemo } from 'react';
import DashboardCards from '../components/diskerasure/Cards';
import DeviceTable from '../components/diskerasure/DeviceTable';
import { Box, Select, Container, Modal, Button, Stack, Typography, useTheme, TextField, MenuItem, IconButton, InputLabel, FormControl } from '@mui/material';
import { TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faWindows as faWindowsBrand, faLinux as faLinuxBrand } from '@fortawesome/free-brands-svg-icons';
import { getCompanyDevices, getCompanyDepartment, getAllDepartments, getAllDevices, addDevices, getSYSAuth, checkDiskChange } from '../apis/axios';
import { getOS, getDrives } from '../apis/agent';
import AuthContext from '../AuthContext';

function DiskErasure() {
  const auth = useContext(AuthContext);
  const theme = useTheme();
  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [addDeviceError, setAddDeviceError] = useState('');
  const [driveData, setDriveData] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [update, setUpdate] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const handleUpdate = () => {
    setUpdate(!update);
  };

  const handleClose = () => {
    setOpenModal(false);
  };


  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const response = await getCompanyDevices({ company_id: auth.admin });
      setDevices(response.data);
      setLoadingDevices(false);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchAllDepartments = async () => {
    try {
      const response = await getAllDepartments(auth.admin);
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map(dept => ({
          value: dept.id,
          label: dept.name,
        }));
        setDepartments(formattedData);
      } else {
        console.error('Invalid response format for departments:', response?.data);
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };


  const fetchDepartments = async () => {
    try {
      const response = await getCompanyDepartment({company_id: auth.admin});
      if (response && Array.isArray(response.data)) {
        const formattedData = response.data.map(dept => ({
          value: dept.id,
          label: dept.name,
        }));
        setDepartments(formattedData);
      } else {
        console.error('Invalid response format for departments:', response?.data);
        setDepartments([]);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (auth.userType === 'superadmin') {
        try {
          setLoadingDevices(true);
          const response = await getAllDevices({ company_id: null });

          setDevices(response.data);
          setLoadingDevices(false);
        } catch (error) {
          console.error('Error fetching devices:', error);
          setDevices([]);
        } finally {
          setLoadingDevices(false);
        }
        await fetchAllDepartments();

      } else if (auth.userType === 'admin' && auth.admin) {
        if (!openModal) {
          await fetchDevices();
        }
        await fetchDepartments();
      } else {
        setDevices([]);
        setDepartments([]);
        setLoadingDevices(false);
      }
    };

    loadData();
  }, [auth.admin, auth.userType, openModal, update]);

  const [formData, setFormData] = useState({
    deviceName: '',
    uuid: '',
    model: '',
    department: '',
    location: '',
    os: '',
    sys_uuid: '',
    disks_info: [],
  });

  useEffect(() => {
    setFormData(prev => ({ ...prev, disks_info: driveData.map(drive => drive.serial_number) }));
  }, [driveData]);

  const [osFetchStatus, setOsFetchStatus] = useState('');
  const [fetchingOs, setFetchingOs] = useState(false);
  const [fetchingDrives, setFetchingDrives] = useState(false);
  const [drivesFetchStatus, setDrivesFetchStatus] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [osFilter, setOsFilter] = useState('All OS Types');

  const departmentOptions = ['All Departments', ...new Set(devices.map(item => item.department_name).filter(Boolean))];
  const osTypes = ['All OS Types', ...new Set(devices.map(item => item.os).filter(Boolean))];

  const filteredData = useMemo(() => {
    return devices.filter(item => {
      const matchesDepartment = departmentFilter === 'All Departments' || item.department_name === departmentFilter;
      const matchesOs = osFilter === 'All OS Types' || item.os === osFilter;
      return matchesDepartment && matchesOs;
    });
  }, [devices, departmentFilter, osFilter]);

  useEffect(() => {
    if (devices.length > 0) {
      //console.log('FilteredData after devices fetched:', filteredData);
    }
  }, [devices, filteredData]);

  const handleInputChange = (field, value) => {
    if (field === 'uuid') {
      setFormData(prev => ({
        ...prev,
        os: '',
        sys_uuid: '',
        [field]: value,
      }));
      setOsFetchStatus('');
      setDrivesFetchStatus('');
      setDriveData([]);
      if (!value) {
        setOsFetchStatus('Please enter a UUID');
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleFetchOs = async () => {
    if (!formData.uuid) {
      setOsFetchStatus('Please enter a UUID');
      return;
    }

    setFetchingOs(true);
    setOsFetchStatus('Fetching OS...');
    try {
      const osResponse = await getOS(formData.uuid);
      const osName = osResponse.data.system || 'Unknown OS';
      const sysUuid = osResponse.data.sys_uuid || 'Unknown UUID';
      setFormData(prev => ({
        ...prev,
        os: osName,
      }));
      setOsFetchStatus('OS Fetched successfully');
      const res = await getSYSAuth({ sys_uuid: sysUuid });
      const encUuid = res.data.encrypted_sys_uuid;
      setFormData(prev => ({
        ...prev,
        sys_uuid: encUuid,
      }));
      setFetchingDrives(true);
      setDrivesFetchStatus('Fetching Disks...');
      const drivesResponse = await getDrives(formData.uuid, encUuid);
      if (drivesResponse && Array.isArray(drivesResponse.data)) {
        setDriveData(drivesResponse.data);
        setDrivesFetchStatus(`Found ${drivesResponse.data.length} Disk${drivesResponse.data.length !== 1 ? 's' : ''}`);
      } else {
        setDriveData([]);
        setDrivesFetchStatus('No Disks found');
      }
    } catch (error) {
      console.error('Error fetching OS or drives:', error);
      if (error.message.includes('OS')) {
        setFormData(prev => ({
          ...prev,
          os: '',
          sys_uuid: '',
        }));
        setOsFetchStatus(`${error.message.includes('Network Error') ? 'Agent is not online' : 'Invalid UUID'}`);
      } else {
        setDriveData([]);
        setDrivesFetchStatus('Failed to fetch Disks');
      }
    } finally {
      setFetchingOs(false);
      setFetchingDrives(false);
    }
  };

  const handleAddDevice = async () => {
    const driveCount = driveData.length;
    const newDevice = {
      name: formData.deviceName,
      uuid: formData.uuid,
      location: formData.location,
      model: formData.model,
      department_id: parseInt(formData.department),
      os: formData.os,
      sys_uuid: formData.sys_uuid,
      disks: `${driveCount} Disk${driveCount !== 1 ? 's' : ''}`,
      disks_info: formData.disks_info,
    };

    try {
      const response = await addDevices(newDevice);
      if (response.data.status === 'success') {
        await fetchDevices();
        setAddDeviceError('');
        setOpenModal(false);
        setFormData({
          deviceName: '',
          uuid: '',
          model: '',
          department: '',
          location: '',
          os: '',
          sys_uuid: '',
          disks_info: [],
        });
        setOsFetchStatus('');
        setDrivesFetchStatus('');
        setDriveData([]);
      } else {
        setAddDeviceError(response.data.message);
      }
    } catch (error) {
      console.error('Error adding device:', error);
      setAddDeviceError(error.response?.data?.message || 'Failed to add device. Please try again.');
    }
  };

  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add('export-button-ripple');

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };



  return (
    <Box display="flex" sx={{ maxWidth: 'none', justifyContent: 'center' }}>
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: 'none' }}>
        <Container
          className="py-4"
          sx={{
            maxWidth: 'none !important',
            overflowX: 'hidden',
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <Box style={{ margin: '0 auto', width: '75.5vw', maxWidth: '75.5vw' }}>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Disk Erasure
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  size="sm"
                  variant="filled"
                  className="export-button"
                  onClick={(e) => {
                    handleRippleEffect(e);
                    setOpenModal(true);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{
                      height: '10px',
                      width: '10px',
                      color: '#fff',
                    }}
                  />
                  <span>Add Devices</span>
                </Button>
              </Stack>
            </Stack>
            <Box className="flex flex-wrap gap-4 md:gap-[30px] mb-6">
              {/* Department Filter */}
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="department-select-label">Department</InputLabel>
                <Select
                  labelId="department-select-label"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  sx={{
                    bgcolor: theme.palette.bg.default,
                    "& .MuiSelect-select": {
                      paddingY: 1,
                      fontFamily: "Nunito Sans, sans-serif",
                      fontSize: 14,
                    },
                  }}
                >
                  <MenuItem value="All Departments">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.value} value={dept.label}>
                      {dept.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* OS Type Filter */}
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="os-select-label">OS Type</InputLabel>
                <Select
                  labelId="os-select-label"
                  value={osFilter}
                  onChange={(e) => setOsFilter(e.target.value)}
                  sx={{
                    bgcolor: theme.palette.bg.default,
                    "& .MuiSelect-select": {
                      paddingY: 1,
                      fontFamily: "Nunito Sans, sans-serif",
                      fontSize: 14,
                    },
                  }}
                >
                  <MenuItem value="All OS Types">All OS Types</MenuItem>
                  {osTypes.map((os) => (
                    <MenuItem key={os} value={os}>
                      {os}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <DashboardCards />
            </Box>
            <Box>
  {loadingDevices ? (
     <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="40vh"
        flexDirection="column"
        gap={2}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '12px',
            border: '6px solid #C084FC',
            borderTopColor: '#F3E8FF',
            animation: 'spin 1s linear infinite',
          }}
        />
        <Typography sx={{ color: '#C084FC', fontWeight: 600 }}>
          Fetching devices...
        </Typography>

        <style>
          {`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
        </style>
      </Box>
  ) : (
    devices.length > 0 ? (
      <DeviceTable data={filteredData} handleUpdate={handleUpdate} />
    ) : (
      <Box>No devices available</Box>
    )
  )}
</Box>

          </Box>
        </Container>
      </Box>
      <Modal open={openModal} onClose={handleClose}>
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            width: { xs: "90vw", sm: 600 },
            maxHeight: "90vh",
            overflowY: "auto",
            mx: "auto",
            mt: "5vh",
            position: "relative",
            boxShadow: 6,
          }}
        >
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight={700}>
              Add New Device
            </Typography>
            <IconButton onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Stack>

          {/* Device Information */}
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Device Information
          </Typography>

          <Stack spacing={2} direction={{ xs: "column", sm: "row" }} mb={2}>
            <TextField
              label="Device Name*"
              fullWidth
              value={formData.deviceName}
              onChange={(e) => handleInputChange("deviceName", e.target.value)}
              placeholder="e.g., WS-IT-001"
            />
            <TextField
              label="UUID*"
              fullWidth
              value={formData.uuid}
              onChange={(e) => handleInputChange("uuid", e.target.value)}
              placeholder="e.g., agent001"
            />
            <TextField
              label="Model*"
              fullWidth
              value={formData.model}
              onChange={(e) => handleInputChange("model", e.target.value)}
              placeholder="e.g., Dell Latitude 7420"
            />
          </Stack>

          <Stack spacing={2} direction={{ xs: "column", sm: "row" }} mb={2}>
            <FormControl fullWidth>
              <InputLabel>Department*</InputLabel>
              <Select
                value={formData.department}
                onChange={(e) => handleInputChange("department", e.target.value)}
                label="Department*"
              >
                {departments.length === 0 ? (
                  <MenuItem disabled value="">
                    No departments available
                  </MenuItem>
                ) : (
                  departments.map((dept) => (
                    <MenuItem key={dept.value} value={dept.value}>
                      {dept.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Location*"
              fullWidth
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
              placeholder="e.g., HQ - Third Floor"
            />
          </Stack>

          <Box my={2} borderTop={1} borderColor="divider" />

          {/* System Operation */}
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            System Operation
          </Typography>

          <Stack spacing={2} mb={2}>
            <TextField
              label="Operating System*"
              fullWidth
              value={formData.os}
              placeholder="OS will be fetched after entering UUID"
              InputProps={{
                readOnly: true,
                endAdornment: formData.os && (
                  <FontAwesomeIcon
                    icon={formData.os.toLowerCase().includes("windows") ? faWindowsBrand : faLinuxBrand}
                    style={{ marginRight: 10, color: formData.os.toLowerCase().includes("windows") ? "#00B0FF" : "#202224" }}
                  />
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={(e) => {
                handleRippleEffect(e);
                handleFetchOs();
              }}
              disabled={fetchingOs || fetchingDrives || !formData.uuid}
            >
              {fetchingOs || fetchingDrives ? "Fetching..." : "Fetch OS"}
            </Button>
            {osFetchStatus && (
              <Typography fontSize={12} color={osFetchStatus.includes("successfully") ? "success.main" : "error.main"}>
                {osFetchStatus}
              </Typography>
            )}
            {drivesFetchStatus && (
              <Typography fontSize={12} color={drivesFetchStatus.includes("Found") ? "success.main" : "error.main"}>
                {drivesFetchStatus}
              </Typography>
            )}
          </Stack>

          <Box my={2} borderTop={1} borderColor="divider" />

          {addDeviceError && (
            <Typography color="error" fontSize={12} mb={2} textAlign="center">
              {addDeviceError}
            </Typography>
          )}

          {/* Footer Button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                bgcolor: "#FF7A00",
                borderRadius: 5,
                px: 4,
                "&:hover": { bgcolor: "#FF7A00" },
              }}
              onClick={(e) => {
                handleRippleEffect(e);
                handleAddDevice();
              }}
              disabled={
                !formData.deviceName ||
                !formData.uuid ||
                !formData.model ||
                !formData.department ||
                !formData.location ||
                !formData.os ||
                !formData.sys_uuid ||
                driveData.length === 0
              }
            >
              Add Device
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default DiskErasure;